const BASKET_AVAILABILITY_ENDPOINT = 'https://yadtxyojtwchchuglufe.supabase.co/functions/v1/basket-availability';
const BASKET_AVAILABILITY_ANON_KEY = 'sb_publishable_M0R8Ruy7inq_OPsvKIT4HQ_FUXnPhpN';
const BASKET_AVAILABILITY_DEBOUNCE_MS = 300;
const BASKET_AVAILABILITY_TIMEOUT_MS = 8000;

// Shared across every <delivery-estimator> instance on the page so that the
// Priority and Standard blocks (which fetch the same basket independently)
// collapse into a single network request instead of firing twice.
const basketAvailabilityInflight = new Map();

function fetchBasketAvailability(items) {
  const key = JSON.stringify(items);
  if (basketAvailabilityInflight.has(key)) {
    return basketAvailabilityInflight.get(key);
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), BASKET_AVAILABILITY_TIMEOUT_MS);

  const promise = fetch(BASKET_AVAILABILITY_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: BASKET_AVAILABILITY_ANON_KEY,
      Authorization: `Bearer ${BASKET_AVAILABILITY_ANON_KEY}`,
    },
    body: JSON.stringify({ items }),
    signal: controller.signal,
  })
    .then((response) => {
      if (!response.ok) throw new Error(`basket-availability responded ${response.status}`);
      return response.json();
    })
    .finally(() => {
      clearTimeout(timeout);
      basketAvailabilityInflight.delete(key);
    });

  basketAvailabilityInflight.set(key, promise);
  return promise;
}

if (!customElements.get('delivery-estimator')) {
  customElements.define(
    'delivery-estimator',
    class DeliveryEstimator extends HTMLElement {
      variantChangeUnsubscriber = undefined;
      optionalSelectionChangeUnsubscriber = undefined;
      quantityUpdateUnsubscriber = undefined;
      _fetchDebounceTimer = undefined;
      _requestSeq = 0;

      connectedCallback() {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
          this.scheduleFetch();
        });
        this.optionalSelectionChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.optionalSelectionChange, () => {
          this.scheduleFetch();
        });
        this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => {
          this.scheduleFetch();
        });
        this.scheduleFetch(0);
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
          this.variantChangeUnsubscriber = undefined;
        }
        if (this.optionalSelectionChangeUnsubscriber) {
          this.optionalSelectionChangeUnsubscriber();
          this.optionalSelectionChangeUnsubscriber = undefined;
        }
        if (this.quantityUpdateUnsubscriber) {
          this.quantityUpdateUnsubscriber();
          this.quantityUpdateUnsubscriber = undefined;
        }
        clearTimeout(this._fetchDebounceTimer);
      }

      scheduleFetch(delay = BASKET_AVAILABILITY_DEBOUNCE_MS) {
        clearTimeout(this._fetchDebounceTimer);
        this._fetchDebounceTimer = setTimeout(() => this.performFetch(), delay);
      }

      async performFetch() {
        const items = this.collectCurrentItems();
        const requestId = ++this._requestSeq;

        if (!items.length) {
          this.clearDeliveryDisplay();
          return;
        }

        let result;
        try {
          result = await fetchBasketAvailability(items);
        } catch (error) {
          console.error('basket-availability request failed', error);
          if (requestId === this._requestSeq) this.clearDeliveryDisplay();
          return;
        }

        // A newer selection change has already superseded this response.
        if (requestId !== this._requestSeq) return;

        this.applyResult(result);
      }

      collectCurrentItems() {
        const root = this.closest('product-info') || document;
        const lines = new Map();

        const addLine = (variantId, quantity) => {
          if (!variantId || variantId === '0') return;
          const qty = quantity > 0 ? quantity : 1;
          lines.set(variantId, (lines.get(variantId) || 0) + qty);
        };

        const mainVariantId = this.getCurrentVariantIdFromForm();
        if (mainVariantId) {
          addLine(mainVariantId, this.getMainQuantity());
        }

        // Covers every selection pattern used across the configurator steps
        // (mattress/add-on radios with a shared quantity stepper, fixed-quantity
        // checkboxes, and the single extra-product toggle) — each renders its
        // selected variant as a checked input carrying a `data-variant` id.
        root.querySelectorAll('input[data-variant]:checked').forEach((input) => {
          addLine(String(input.dataset.variant), this.resolveOptionalQuantity(input));
        });

        return Array.from(lines, ([variant_id, quantity]) => ({ variant_id, quantity }));
      }

      resolveOptionalQuantity(input) {
        const sharedQtyInput = input.closest('optional-items')?.querySelector('[name="extra-quantity"]');
        if (sharedQtyInput) return this.parsePositiveInt(sharedQtyInput.value);

        if (input.dataset.itemQuantity) return this.parsePositiveInt(input.dataset.itemQuantity);

        return this.parsePositiveInt(input.dataset.quantity);
      }

      getMainQuantity() {
        const form = this.getProductFormElement();
        const input = form?.querySelector('input[name="quantity"]');
        return this.parsePositiveInt(input?.value);
      }

      parsePositiveInt(value) {
        const parsed = parseInt(value, 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
      }

      applyResult({ priority_date, priority_headroom, standard_date, standard_headroom, contact_us } = {}) {
        const deliveryElement = this.querySelector('.delivery');
        const deliveryMain = deliveryElement?.querySelector('.delivery_main');
        const deliveryDateSpan = deliveryElement?.querySelector('.delivery_date');
        const availabilityEl = deliveryElement?.querySelector('.delivery-availability');
        const contactEl = deliveryElement?.querySelector('.delivery-contact');

        // The server-rendered fallback hides this block entirely when the
        // (separate, day-count) fallback metafield is blank, regardless of
        // whether real delivery data exists. Once we have a live answer,
        // that SSR gate no longer applies.
        deliveryElement?.classList.remove('hidden');

        if (contact_us || (!priority_date && !standard_date)) {
          deliveryMain?.classList.add('hidden');
          availabilityEl?.classList.add('hidden');
          contactEl?.classList.remove('hidden');
          this.clearDeliveryProperty();
          return;
        }

        contactEl?.classList.add('hidden');
        deliveryMain?.classList.remove('hidden');

        const effectiveDate = priority_date || standard_date;
        const effectiveHeadroom = priority_date ? priority_headroom : standard_headroom;

        const formatted = this.formatDeliveryRange(effectiveDate);
        if (deliveryDateSpan && formatted) {
          deliveryDateSpan.textContent = formatted;
          deliveryDateSpan.dataset.rawDeliveryDate = effectiveDate;
        }
        this.upsertDeliveryProperty(formatted);

        if (availabilityEl) {
          if (effectiveHeadroom !== null && effectiveHeadroom !== undefined) {
            const onlyLeftTemplate = availabilityEl.dataset.onlyLeftTemplate || 'Only __COUNT__ left';
            availabilityEl.innerHTML = '<span class="delivery-live-dot"></span>';
            availabilityEl.appendChild(document.createTextNode(onlyLeftTemplate.replace('__COUNT__', effectiveHeadroom)));
            availabilityEl.classList.remove('hidden');
          } else {
            availabilityEl.classList.add('hidden');
          }
        }
      }

      clearDeliveryDisplay() {
        const deliveryElement = this.querySelector('.delivery');
        deliveryElement?.querySelector('.delivery_main')?.classList.add('hidden');
        deliveryElement?.querySelector('.delivery-availability')?.classList.add('hidden');
        deliveryElement?.querySelector('.delivery-contact')?.classList.add('hidden');
        this.clearDeliveryProperty();
      }

      getCurrentVariantIdFromForm() {
        const form = this.getProductFormElement();
        const input = form?.querySelector('[name="id"]');
        const value = input?.value;

        const id = Number(value);
        return Number.isFinite(id) && id > 0 ? String(id) : null;
      }

      get deliveryPropertyEnabled() {
        // Set data-disable-property on the element to turn off property injection.
        return !('disableProperty' in this.dataset);
      }

      get deliveryPropertyName() {
        // This becomes the line item property label in cart/order.
        return this.dataset.deliveryPropertyName || 'Delivery date';
      }

      get deliveryPropertyInputName() {
        return `properties[${this.deliveryPropertyName}]`;
      }

      getProductFormElement() {
        const root = this.closest('product-info') || document;

        // Prefer the closest product section.
        const form = root.querySelector('product-form form');
        if (form) return form;

        // Fallback for other templates/quick add.
        return document.querySelector('product-form form');
      }

      upsertDeliveryProperty(value) {
        if (!this.deliveryPropertyEnabled) return;
        const form = this.getProductFormElement();
        if (!form) return;

        const safeValue = typeof value === 'string' ? value.trim() : '';
        if (!safeValue) {
          this.clearDeliveryProperty();
          return;
        }

        const inputName = this.deliveryPropertyInputName;
        let input = form.querySelector(`input[name="${CSS.escape(inputName)}"]`);

        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = inputName;

          // Keep consistency with existing code that inserts hidden inputs before product-id when present.
          const productIdElement = form.querySelector('[name="product-id"]');
          if (productIdElement) {
            form.insertBefore(input, productIdElement);
          } else {
            form.appendChild(input);
          }
        }

        input.value = safeValue;
      }

      clearDeliveryProperty() {
        if (!this.deliveryPropertyEnabled) return;
        const form = this.getProductFormElement();
        if (!form) return;

        const inputName = this.deliveryPropertyInputName;
        const input = form.querySelector(`input[name="${CSS.escape(inputName)}"]`);
        if (input) input.value = '';
      }

      formatDeliveryRange(yyyyMmDd) {
        const start = this.parseYyyyMmDdToLocalDate(yyyyMmDd);
        if (!start) return '';

        // Window spans 3 business days inclusive of the start date
        // (e.g. Fri start -> Mon, Tue -> ends Tue).
        const end = this.addBusinessDays(start, 2);
        return this.formatDateRange(start, end);
      }

      addBusinessDays(date, businessDays) {
        const result = new Date(date);
        let added = 0;
        while (added < businessDays) {
          result.setDate(result.getDate() + 1);
          const day = result.getDay();
          if (day !== 0 && day !== 6) added++;
        }
        return result;
      }

      formatDateRange(dateStart, dateEnd) {
        const locale = document.documentElement.lang || 'en-US';
        const dayStart = dateStart.getDate();
        const monthStart = dateStart.toLocaleString(locale, { month: 'short' });
        const dayEnd = dateEnd.getDate();
        const monthEnd = dateEnd.toLocaleString(locale, { month: 'short' });

        if (monthStart === monthEnd) {
          return `${dayStart} - ${dayEnd} ${monthStart}`;
        }

        return `${dayStart} ${monthStart} - ${dayEnd} ${monthEnd}`;
      }

      parseYyyyMmDdToLocalDate(yyyyMmDd) {
        if (!yyyyMmDd || typeof yyyyMmDd !== 'string') return null;

        // Expected format: 2026-03-19
        const match = yyyyMmDd.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!match) return null;

        const year = Number(match[1]);
        const monthIndex = Number(match[2]) - 1; // 0-based
        const day = Number(match[3]);

        const date = new Date(year, monthIndex, day);
        if (Number.isNaN(date.getTime())) return null;

        return date;
      }
    }
  );
}
